import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import {
  CreateAndAssignGoalConfigurationInput,
  GoalConfigurationTemplateTableItemFragment,
  useTeamsFilterForResourcesQuery,
} from "apps/web/src/types";
import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  GoalConfigurationFormSchema,
  GoalConfigurationFormType,
} from "../../Athlete/GoalTracker/GoalConfiguration/GoalConfigurationSchema";
import { useAdvancedSelection } from "../useAdvancedSelection";
import { assignGoalConfigurationTemplateFormDefaultValues, createAndAssignGoalConfigurationInput } from "../utils";
import AssignGoalConfigFromTemplateDialogContent from "./AssignGoalConfigFromTemplateDialogContent";

interface AssignGoalConfigFromTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  goalConfigTemplate: GoalConfigurationTemplateTableItemFragment;
  onSubmit: (input: CreateAndAssignGoalConfigurationInput) => void;
}

const AssignGoalConfigFromTemplateDialog = ({ open, onClose, goalConfigTemplate, onSubmit }: AssignGoalConfigFromTemplateDialogProps) => {
  const [assignToError, setAssignToError] = useState(false);
  const [advancedSelectionOpen, setAdvancedSelectionOpen] = useState(false);
  const form = useForm<GoalConfigurationFormType>({
    defaultValues: assignGoalConfigurationTemplateFormDefaultValues(goalConfigTemplate),
    resolver: zodResolver(GoalConfigurationFormSchema),
    mode: "onChange",
  });
  const [advancedSelectState, advancedSelectDispatch] = useAdvancedSelection();

  const { data: teamsData } = useTeamsFilterForResourcesQuery();
  const allTeams = teamsData ? teamsData.teams : [];

  const submit = (goal: GoalConfigurationFormType) => {
    const assignGoalConfigurationInput = createAndAssignGoalConfigurationInput(goal, goalConfigTemplate, advancedSelectState);
    const validAthleteAssigned =
      (assignGoalConfigurationInput.teamIds && assignGoalConfigurationInput.teamIds.length > 0) ||
      assignGoalConfigurationInput.advancedSelectionInput !== null;

    if (!validAthleteAssigned) {
      setAssignToError(true);
      return;
    }

    onSubmit(assignGoalConfigurationInput);
    handleClose();
  };

  const handleClose = () => {
    advancedSelectDispatch({ type: "ClearAdvancedSelectionAction" });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h3">Assign Goal</Typography>
          <IconButton onClick={handleClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <AssignGoalConfigFromTemplateDialogContent
          goalConfigTemplate={goalConfigTemplate}
          form={form}
          advancedSelectState={advancedSelectState}
          advancedSelectDispatch={advancedSelectDispatch}
          allTeams={allTeams}
          assignToError={assignToError}
          setAssignToError={setAssignToError}
          advancedSelectionOpen={advancedSelectionOpen}
          setAdvancedSelectionOpen={setAdvancedSelectionOpen}
        />
      </DialogContent>
      <DialogActions>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={form.handleSubmit(submit)}>Assign</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AssignGoalConfigFromTemplateDialog;
